import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import type { AdminUser, Customer } from "@attendik/types/admin-schemas";

import { api } from "../api/client.js";
import { isApiError } from "../api/errors.js";
import { PageHeader } from "../components/AppLayout.js";
import { Button } from "../components/ui/button.js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../components/ui/dialog.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table.js";
import { ErrorState, LoadingState } from "../components/states.js";
import { formatDate } from "../lib/format.js";
import { useAsync } from "../lib/useAsync.js";
import {
  StatusBadge,
  accountStatusVariant,
  customerStatusVariant
} from "./shared/pills.js";

interface DetailData {
  customer: Customer;
  admins: AdminUser[];
}

/** Customer detail — info, status toggle, admin list, invite (build plan P2-T6). */
export function CustomerDetailPage(): JSX.Element {
  const { customerId = "" } = useParams();
  const [inviting, setInviting] = useState(false);

  const { data, loading, error, reload } = useAsync<DetailData>(async () => {
    const [customer, allAdmins] = await Promise.all([
      api.getCustomer(customerId),
      api.listAdminUsers()
    ]);
    return {
      customer,
      admins: allAdmins.filter((u) => u.customer_id === customerId)
    };
  }, [customerId]);

  if (loading) {
    return (
      <>
        <PageHeader title="Customer" />
        <LoadingState />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader title="Customer" />
        <ErrorState message={error ?? "Failed to load."} onRetry={reload} />
      </>
    );
  }

  const { customer, admins } = data;

  const toggleStatus = async (): Promise<void> => {
    await api.updateCustomer(customer.id, {
      status: customer.status === "ACTIVE" ? "DISABLED" : "ACTIVE"
    });
    reload();
  };

  return (
    <>
      <PageHeader
        title={customer.name}
        description={
          <Link to="/customers" className="text-accent hover:underline">
            ← All customers
          </Link>
        }
        actions={
          <Button
            variant={customer.status === "ACTIVE" ? "outline" : "default"}
            onClick={toggleStatus}
          >
            {customer.status === "ACTIVE" ? "Disable" : "Enable"}
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-0 text-sm">
            <DetailRow label="Name" value={customer.name} />
            <DetailRow
              label="Slug"
              value={<span className="font-mono text-xs">{customer.slug}</span>}
            />
            <DetailRow
              label="Status"
              value={
                <StatusBadge variant={customerStatusVariant(customer.status)}>
                  {customer.status}
                </StatusBadge>
              }
            />
            <DetailRow label="Created" value={formatDate(customer.created_at)} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle>Customer admins</CardTitle>
            <Button size="sm" onClick={() => setInviting(true)}>
              Invite admin
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {admins.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                No customer admins yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell className="font-medium">{admin.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {admin.email}
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={accountStatusVariant(admin.status)}>
                          {admin.status}
                        </StatusBadge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {inviting ? (
        <InviteAdminDialog
          customerId={customer.id}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function DetailRow({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

function InviteAdminDialog({
  customerId,
  onClose,
  onInvited
}: {
  customerId: string;
  onClose: () => void;
  onInvited: () => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.createAdminUser({
        email: email.trim(),
        name: name.trim(),
        role: "customer_admin",
        customer_id: customerId,
        password
      });
      onInvited();
    } catch (err) {
      if (isApiError(err) && err.code === "EMAIL_TAKEN") {
        setError("An admin with that email already exists.");
      } else {
        setError("Could not invite the admin. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy="invite-admin-title">
      <DialogHeader
        id="invite-admin-title"
        title="Invite customer admin"
        description="The new admin can sign in immediately with this password."
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="space-y-1.5">
            <Label htmlFor="invite-name">Name</Label>
            <Input
              id="invite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-password">Initial password</Label>
            <Input
              id="invite-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">Minimum 12 characters.</p>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Inviting…" : "Invite"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
